// module.power-budget (contract §4.8.1): PowerBudgetModule (modules.tsx), its
// three bodies chosen by the feed's `layout`: the state of charge as the hero
// beside the charge flow, its 24 h trend (on [0, 100], the 50% line dashed)
// and the chips (time remaining, the bank's voltage, health, temperature, the
// charge sources); or, with no state-of-charge sensor, the flow as the hero
// and the voltage; or the voltage alone. The two sensorless bodies say why.
//
// Where the page differs: the trend's caption is the window's own label
// ("last 24 h", `data-tc-window-text`), since a page writes no number of its
// own (§9.4).
//
// The Marina look (TOM-678): the trend in power's colour with its fading
// area and its "now" dot, and the range chip in the trend's head in place of
// the window's words.

import { css, h, rangeChip } from "./kit.js";
import { F, R, S } from "./module-kit.js";

const SOURCES = 4;

const val = (field, unit, size, extra = {}) =>
  h(
    "span",
    { class: `rd-row rd-${size}` },
    extra.glyph ? h("span", { class: "mo-g pw-g", ...F("flow-dir") }) : "",
    h("span", { class: "rd-v", ...F(field) }),
    typeof unit === "string"
      ? h("span", { class: "rd-u" }, unit)
      : h("span", { class: "rd-u", ...F(unit.field) }),
  );

// A chip is its parts side by side, each on the chip's fill: a chip whose
// value is missing hides whole, though a page cannot style a parent by its
// child, because the value comes first in the markup and the rest follow it
// (`order` puts the label first on screen).
function sources() {
  const out = [];
  for (let k = 0; k < SOURCES; k += 1)
    out.push(
      h(
        "span",
        { class: "pw-c" },
        h("b", R("source-value", k)),
        h("i", { class: "c-l", ...R("source-label", k) }),
        h("i", R("source-unit", k)),
        h("i", { class: "pw-st", ...R("source-state", k) }),
        h("i", { class: "c-e" }),
      ),
    );
  return out;
}

const chip = (label, field, unit) =>
  h(
    "span",
    { class: "pw-c" },
    h("b", F(field)),
    typeof label === "string"
      ? h("i", { class: "c-l" }, label)
      : h("i", { class: "c-l", ...F(label.field) }),
    unit === null
      ? ""
      : typeof unit === "string"
        ? h("i", { class: "c-u" }, unit)
        : h("i", { class: "c-u", ...F(unit.field) }),
    h("i", { class: "c-e" }),
  );

export function modulePowerBudget() {
  const soc = h(
    "div",
    { class: "pw-b pw-soc" },
    h(
      "div",
      { class: "pw-row" },
      h(
        "div",
        { class: "rd" },
        h("span", { class: "pw-l" }, "State of charge"),
        val("soc", "%", "xl"),
      ),
      h(
        "div",
        { class: "rd mo-end" },
        h("span", { class: "pw-l", ...F("flow-label") }),
        val("flow", { field: "flow-unit" }, "md", { glyph: true }),
        h("span", { class: "mo-sub", ...F("flow-sub") }),
      ),
    ),
    h(
      "div",
      { class: "pw-tr", "data-tc-window": "24h" },
      h(
        "div",
        { class: "pw-th" },
        h("span", { class: "pw-cap", ...F("soc-delta") }),
        rangeChip(),
      ),
      h(
        "svg",
        {
          class: "mo-lane pw-lane",
          "data-tc-fit": "soc",
          "aria-hidden": "true",
        },
        h("line", { class: "pw-50", x2: 1 }),
        h("path", {
          class: "ar",
          ...S("soc", {
            "data-tc-shape": "area",
            "data-tc-y": "feed",
            "data-tc-group": "soc",
            "data-tc-inset": "3 4 3 4",
          }),
        }),
        h("path", {
          class: "ln",
          ...S("soc", {
            "data-tc-y": "feed",
            "data-tc-group": "soc",
            "data-tc-inset": "3 4 3 4",
          }),
        }),
        h("circle", { class: "pw-now", r: 3, ...F("soc-now") }),
      ),
    ),
    h(
      "div",
      { class: "pw-chips" },
      chip("Remaining", "remaining", null),
      chip({ field: "voltage-label" }, "voltage", "V"),
      chip("Health", "health", "%"),
      chip("Temp", "temperature", { field: "temperature-unit" }),
      sources(),
    ),
  );
  const flow = h(
    "div",
    { class: "pw-b pw-flow" },
    h(
      "div",
      { class: "pw-row" },
      h(
        "div",
        { class: "rd" },
        h("span", { class: "pw-l", ...F("flow-label") }),
        val("flow", { field: "flow-unit" }, "xl", { glyph: true }),
        h("span", { class: "mo-sub", ...F("flow-sub") }),
      ),
      h(
        "div",
        { class: "rd" },
        h("span", { class: "pw-l", ...F("voltage-label") }),
        val("voltage", "V", "md"),
      ),
    ),
    h("p", { class: "pw-p", ...F("projection") }),
  );
  const volts = h(
    "div",
    { class: "pw-b pw-volt" },
    h(
      "div",
      { class: "rd" },
      h("span", { class: "pw-l", ...F("voltage-label") }),
      val("voltage", "V", "xl"),
    ),
    h("p", { class: "pw-p", ...F("projection") }),
  );
  return {
    lane: true,
    kind: true,
    html: [h("i", { class: "mo-flag", ...F("layout") }), soc, flow, volts],
    css: css(`
      .pw-b { display: none; flex-direction: column; gap: 14px; flex: 1 1 auto; min-height: 0; }
      .pw-soc, .mo-flag[data-state=flow] ~ .pw-flow, .mo-flag[data-state=voltage] ~ .pw-volt { display: flex; }
      .mo-flag[data-state=flow] ~ .pw-soc, .mo-flag[data-state=voltage] ~ .pw-soc { display: none; }
      .pw-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; align-items: start; }
      .pw-l { font-size: 13px; font-weight: 500; color: var(--muted); white-space: nowrap; }
      .pw-b .rd-v { font-weight: 700; }
      .pw-b .rd-v[data-tone=warn] { color: var(--attention); }
      .pw-b .rd-v[data-tone=bad] { color: var(--danger); }
      /* At night a warning value keeps the text colour: --attention under the night tint reads below the app's own values (§9.6, TOM-665). */
      html[data-theme=night] .pw-b .rd-v[data-tone=warn] { color: var(--text); }
      .pw-g[data-state=charging] { color: var(--good); }
      .pw-tr { display: flex; flex-direction: column; gap: 8px; flex: 1 1 auto; min-height: 0; }
      .pw-th { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
      .pw-cap { font-size: 12px; font-weight: 500; color: var(--muted); }
      .pw-lane { min-height: 64px; height: 96px; }
      .pw-50 { stroke: var(--grid); stroke-width: 1; stroke-dasharray: 3 4; vector-effect: non-scaling-stroke;
        transform: translate(4px, calc(3px + 0.5 * (var(--tc-h,96) - 6) * 1px)) scaleX(calc(var(--tc-w,300) - 8)); }
      .pw-now { fill: var(--_c); stroke: var(--background); stroke-width: 2;
        transform: translate(calc(4px + var(--x,0) * (var(--tc-w,300) - 8) * 1px), calc(3px + (1 - var(--y,0)) * (var(--tc-h,96) - 6) * 1px)); }
      .pw-chips { display: flex; flex-wrap: wrap; gap: 8px; }
      .pw-c { display: inline-flex; align-items: stretch; white-space: nowrap; font-size: 12px; color: var(--muted); }
      .pw-c > * { display: flex; align-items: center; font-style: normal; background: var(--surface); padding: 3px 0; line-height: 18px; }
      .pw-c .c-l { order: 1; padding-left: 10px; padding-right: 5px; border-radius: 999px 0 0 999px; }
      .pw-c b { order: 2; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
      .pw-c .c-u { order: 3; padding-left: 3px; }
      .pw-c .c-e { order: 4; padding-right: 10px; border-radius: 0 999px 999px 0; }
      .pw-c .pw-st { display: none; }
      .pw-c b:empty, .pw-c b:empty ~ *, .pw-c b[data-quality=missing], .pw-c b[data-quality=missing] ~ * { display: none; }
      .pw-c .c-l:empty, .pw-c .c-l:empty ~ * { display: none; }
      .pw-p { font-size: 12px; color: var(--muted); }
      .pw-p:empty { display: none; }
      /* The bento's cell (dashboard.css, nm-bento): wide and short, so the
         body goes horizontal, the state of charge | its trend | the flow,
         the chips under the trend. */
      html[data-slot=power-budget] .pw-soc { display: grid; grid-template-columns: auto minmax(140px, 1fr) auto;
        grid-template-rows: minmax(0, 1fr) auto; gap: 8px 18px; }
      html[data-slot=power-budget] .pw-soc > .pw-row { display: contents; }
      html[data-slot=power-budget] .pw-soc .pw-row > .rd:first-child { grid-column: 1; grid-row: 1 / -1; align-self: center; }
      html[data-slot=power-budget] .pw-soc .pw-row > .rd:last-child { grid-column: 3; grid-row: 1 / -1; }
      html[data-slot=power-budget] .pw-soc > .pw-tr { grid-column: 2; grid-row: 1; overflow: hidden; container: pw-tr / size; }
      html[data-slot=power-budget] .pw-soc > .pw-chips { grid-column: 2; grid-row: 2; }
      html[data-slot=power-budget] .pw-lane { min-height: 0; height: auto; }
      html[data-slot=power-budget] .pw-l { font-size: 12px; }
      @container pw-tr (height < 44px) { .pw-tr > * { display: none; } }
    `),
  };
}
